// Helpers for working with the new `members` GraphQL query shape.
//
// The `members` query returns a relay-style connection:
//   { members: { edges: [{ node: Member, cursor }], pageInfo } }
//
// Each Member node has:
//   { id, user, relationshipKind, viewerRole, assignedStaffRole, assignedStaffNote }
//
// This replaces the old `me.assignedMembers` which returned a list of UserStaff
// wrappers shaped like: { id, role, member: User }
// The main shape changes:
//   .member → .user
//   .role   → .assignedStaffRole

/**
 * Flatten a `members` GraphQL connection response into a plain array of Member nodes.
 * @param {object} data — Apollo query data, e.g. `{ members: { edges: [...] } }`
 * @returns {Array<object>} — array of Member nodes, or [] when missing
 */
export function flattenMembersConnection(data) {
  const edges = data?.members?.edges;
  if (!edges) return [];
  return edges.map(edge => edge.node);
}

/**
 * Find a Member in a members array by the underlying User id (e.g. "User:123").
 * Note: the Member has its own id ("Member:123"), distinct from the user id.
 * Callers consistently look up patients by User id.
 * @param {Array<object>|null|undefined} members — flattened members array
 * @param {string|null|undefined} userId — User:<n> node id to find
 * @returns {object|undefined} — the matching Member node, or undefined
 */
export function findMemberByUserId(members, userId) {
  if (!members || !userId) return undefined;
  return members.find(m => m?.user?.id === userId);
}
